import { TextEncodingUtil } from '@onshape/www-inertia';
import HashUtil from '~/utils/HashUtil';

const interactionEventTypes = ['keyup', 'click', 'touchend', 'input'] as const;
type InteractionEventType = (typeof interactionEventTypes)[number];

type InputInteraction = {
  t: InteractionEventType;
  c: { tl: { x: number; y: number }; br: { x: number; y: number } };
  dt: number;
  n: string;
};

type ShaderTestResult = {
  shaderResult: string;
  shaderDurationMs: number;
};

type ToSend = {
  i: InputInteraction[];
  pn: number;
  pfl: number;
  gl: string;
  g: string;
  sr: ShaderTestResult | string;
  tzo: number;
  dr: number;
  lc: string;
};

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export default class UxProfileUtil {
  private static readonly tic = Date.now();
  private static readonly interactions: InputInteraction[] = [];
  private static readonly interactionEventTypes = interactionEventTypes;

  private static gpu: string | null = null;

  private static shaderTestResult: ShaderTestResult | null = null;

  public static watchInteractions() {
    try {
      if (!document || !document.body) {
        return;
      }
      Array.from(document.body.querySelectorAll('[form-id]')).forEach((form) => {
        UxProfileUtil.watchFormInputs(form);
      });

      UxProfileUtil.initAsync();
    } catch {
      // ignore
    }
  }

  private static async initAsync() {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    UxProfileUtil.getGpu()
      .then((gpu) => {
        UxProfileUtil.gpu = gpu;
      })
      .catch(() => {
        UxProfileUtil.gpu = 'throw-gpu';
      });

    UxProfileUtil.runShaderTest()
      .then((result) => {
        console.log('Shader test result:', result);
        UxProfileUtil.shaderTestResult = result;
      })
      .catch(() => {
        UxProfileUtil.shaderTestResult = { shaderResult: 'failed', shaderDurationMs: -1 };
      });
  }

  private static watchFormInputs(form: Element) {
    const startTime = Date.now();
    const maxWait = 15 * 1000;

    const checkForInputs = () => {
      const inputs = form.querySelectorAll('input, select, textarea, button');
      if (inputs.length > 0) {
        inputs.forEach((input) => {
          UxProfileUtil.watchInputEvents(input);
        });

        const watchedEvents = UxProfileUtil.interactionEventTypes;
        watchedEvents.forEach((eventType) => {
          form.addEventListener(eventType, (evt) => {
            UxProfileUtil.recordInteraction(evt);
          });
        });
        return;
      }

      if (Date.now() - startTime < maxWait) {
        setTimeout(checkForInputs, 100);
      }
    };

    checkForInputs();
  }

  private static watchInputEvents(input: Element) {
    UxProfileUtil.interactionEventTypes.forEach((eventType) => {
      input.addEventListener(eventType, (evt) => {
        UxProfileUtil.recordInteraction(evt);
      });
    });
  }

  private static recordInteraction(evt: Event) {
    const target = evt.target as HTMLElement;

    if (!target) {
      return;
    }

    const elementName = UxProfileUtil.getElementName(target);

    if (!elementName) {
      return;
    }
    if (UxProfileUtil.hasInteraction(elementName)) {
      return;
    }

    const interaction = UxProfileUtil.buildInteraction(evt, target, elementName);
    UxProfileUtil.interactions.push(interaction);
  }

  private static getElementName(target: HTMLElement) {
    return target.getAttribute('name') || target.getAttribute('id') || target.tagName;
  }

  private static hasInteraction(elementName: string) {
    return UxProfileUtil.interactions.some((interaction) => interaction.n === elementName);
  }

  private static buildInteraction(
    evt: Event,
    target: HTMLElement,
    elementName: string
  ): InputInteraction {
    const rect = target.getBoundingClientRect();

    return {
      t: evt.type as InteractionEventType,
      c: {
        tl: { x: parseFloat(rect.left.toFixed(2)), y: parseFloat(rect.top.toFixed(2)) },
        br: { x: parseFloat(rect.right.toFixed(2)), y: parseFloat(rect.bottom.toFixed(2)) },
      },
      n: elementName,
      dt: Date.now() - UxProfileUtil.tic,
    };
  }

  private static async getGpu() {
    try {
      if (!navigator) {
        return 'no-navigator';
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (!(navigator as any).gpu) {
        return 'no-gpu';
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const adapter = await (navigator as any).gpu.requestAdapter();
      if (!adapter) {
        return 'no-gpu-adapter';
      }
      const limits = adapter.limits;
      const tracked: Array<number> = [];
      tracked.push(limits.maxTextureArrayLayers);
      tracked.push(limits.maxTextureDimension1D);
      tracked.push(limits.maxTextureDimension2D);
      tracked.push(limits.maxTextureDimension3D);
      const result = tracked.join('-');
      return result;
    } catch {
      return 'throw-gpu';
    }
  }

  private static getGl() {
    try {
      const canvas = document.createElement('canvas');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const gl: any = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (!gl) {
        return 'no-gl';
      }
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        return gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || 'no-mask';
      } else {
        return gl.getParameter(gl.RENDERER) || 'no-renderer';
      }
    } catch {
      // ignore
    }

    return 'throw-gl';
  }

  public static getInteractions() {
    try {
      return UxProfileUtil.innerGetInteractions();
    } catch {
      try {
        const toSend = {
          isFailed: true,
          now: Date.now(),
          userAgent: navigator?.userAgent || 'no-user-agent',
        };
        const stringified = JSON.stringify(toSend);
        const uxProfile = TextEncodingUtil.base64Encode(stringified);
        const uxProfileSig = HashUtil.cyrb53(uxProfile);
        return {
          uxProfile,
          uxProfileSig,
        };
      } catch {
        return {
          uxProfile: 'failed-to-build-ux-profile',
          uxProfileSig: 'failed-to-build-ux-profile',
        };
      }
    }
  }

  public static async runShaderTest(): Promise<ShaderTestResult> {
    try {
      return UxProfileUtil.innerRunShaderTest();
    } catch {
      return { shaderResult: 'failed', shaderDurationMs: -1 };
    }
  }

  public static renderShaderToCanvas(selector: string): void {
    const el = document.querySelector(selector);
    if (!el) {
      throw new Error(`UxProfileUtil.renderShaderToCanvas: no element found for "${selector}"`);
    }
    if (!(el instanceof HTMLCanvasElement)) {
      throw new Error(
        `UxProfileUtil.renderShaderToCanvas: element matched by "${selector}" is not a <canvas>`
      );
    }
    UxProfileUtil.runShaderProgram(el);
  }

  private static runShaderProgram(canvas: HTMLCanvasElement): WebGLRenderingContext {
    const W = canvas.width || 512;
    const H = canvas.height || 512;
    canvas.width = W;
    canvas.height = H;

    const gl = canvas.getContext('webgl') as WebGLRenderingContext | null;
    if (!gl) {
      throw new Error('UxProfileUtil: WebGL not available');
    }

    const vertSrc = `
      attribute vec2 a_pos;
      void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }
    `;
    const fragSrc = UxProfileUtil.shaderFragSrc();

    const compileShader = (type: number, src: string): WebGLShader => {
      const shader = gl.createShader(type)!;
      gl.shaderSource(shader, src);
      gl.compileShader(shader);
      return shader;
    };

    const program = gl.createProgram()!;
    gl.attachShader(program, compileShader(gl.VERTEX_SHADER, vertSrc));
    gl.attachShader(program, compileShader(gl.FRAGMENT_SHADER, fragSrc));
    gl.linkProgram(program);
    gl.useProgram(program);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const posLoc = gl.getAttribLocation(program, 'a_pos');
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    gl.uniform2f(gl.getUniformLocation(program, 'u_resolution'), W, H);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    return gl;
  }

  private static shaderFragSrc(): string {
    return `
      precision highp float;
      uniform vec2 u_resolution;
      void main() {
        vec2 uv  = gl_FragCoord.xy / u_resolution;
        vec2 c   = (uv - vec2(0.5)) * 3.5;
        vec2 z   = vec2(0.0);
        float iter = 256.0;
        for (int i = 0; i < 256; i++) {
          if (dot(z, z) > 4.0) {
            iter = float(i);
            break;
          }
          z = vec2(z.x * z.x - z.y * z.y + c.x,
                   2.0 * z.x * z.y           + c.y);
        }
        float t = iter / 256.0;
        float v = sin(t  * 3.14159265358979323846)
                * cos(uv.x * 6.28318530717958647692)
                + sin(uv.y * 2.71828182845904523536)
                * cos(t    * 1.61803398874989484820);
        gl_FragColor = vec4(
          fract(v),
          fract(v * 1.61803398874989484820),
          fract(v * 2.71828182845904523536),
          1.0
        );
      }
    `;
  }

  private static innerRunShaderTest(): ShaderTestResult {
    const W = 256;
    const H = 256;

    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;

    const tic = performance.now();
    const gl = UxProfileUtil.runShaderProgram(canvas);
    // gl.finish() forces the GPU pipeline to flush — without it timing is meaningless
    gl.finish();
    const shaderDurationMs = parseFloat((performance.now() - tic).toFixed(6));

    // Read back a sparse grid of pixels (every 16th row/col = 1024 samples).
    // Sparse sampling is fast but still captures per-GPU precision differences.
    const pixels = new Uint8Array(W * H * 4);
    gl.readPixels(0, 0, W, H, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

    const samples: number[] = [];
    const stride = 16;
    for (let row = 0; row < H; row += stride) {
      for (let col = 0; col < W; col += stride) {
        const base = (row * W + col) * 4;
        samples.push(pixels[base] ?? 0, pixels[base + 1] ?? 0, pixels[base + 2] ?? 0);
      }
    }

    const shaderResult = HashUtil.cyrb53(samples.join(',')).toString();
    return { shaderResult, shaderDurationMs };
  }

  private static innerGetInteractions() {
    let toSendInteractions = UxProfileUtil.interactions;
    const preFilterLength = UxProfileUtil.interactions.length;

    // Limit to 5 interactions: always include first and last, with 3 equally-spaced in between
    if (toSendInteractions.length > 5) {
      const sampled: InputInteraction[] = [];
      const step = (toSendInteractions.length - 1) / 4;

      for (let ii = 0; ii <= 4; ii += 1) {
        const index = Math.floor(ii * step);
        if (toSendInteractions[index]) {
          sampled.push(toSendInteractions[index]);
        }
      }

      toSendInteractions = sampled;
    }

    const { heartbeat } = useCrossSession();

    const hbn = heartbeat.value?.now || 0;
    const dr = Date.now() - hbn;

    const toSend: ToSend = {
      i: toSendInteractions,
      pn: parseFloat(performance.now().toFixed(2)),
      pfl: preFilterLength,
      gl: UxProfileUtil.getGl(),
      g: UxProfileUtil.gpu || 'gpu-not-ready',
      sr: UxProfileUtil.shaderTestResult || 'shader-not-ready',
      dr,
      tzo: new Date().getTimezoneOffset() / 60,
      lc: navigator?.language || 'no-locale',
    };

    const stringified = JSON.stringify(toSend);
    const uxProfile = TextEncodingUtil.base64Encode(stringified);
    const uxProfileSig = HashUtil.cyrb53(uxProfile);

    const signedInteractions = {
      uxProfile,
      uxProfileSig,
    };
    return signedInteractions;
  }
}
